export interface PingButtonOptions {
  readonly label?: string;
  readonly title?: string;
  readonly ariaLabel?: string;
  readonly className?: string;
}

const DEFAULT_LABEL = "Ping";
// Reuses the existing ui-shell base button classes (crest-btn / crest-btn--small),
// matching CopyButton / PasteButton so per-app CSS can layer on top via `className`.
const BASE_CLASSES: readonly string[] = ["crest-btn", "crest-btn--small"];

export class PingButton {
  private readonly button: HTMLButtonElement;
  private parent: HTMLElement | null = null;
  private clickHandler: (() => void) | null = null;
  private visible = false;
  private disposed = false;

  private readonly handleClick = (): void => {
    if (this.clickHandler !== null) this.clickHandler();
  };

  constructor(options: PingButtonOptions = {}) {
    const btn = document.createElement("button");
    btn.type = "button";

    const classes = [...BASE_CLASSES];
    if (options.className !== undefined && options.className.length > 0) {
      classes.push(options.className);
    }
    btn.className = classes.join(" ");

    btn.textContent = options.label ?? DEFAULT_LABEL;
    if (options.title !== undefined) btn.title = options.title;
    if (options.ariaLabel !== undefined) btn.setAttribute("aria-label", options.ariaLabel);

    btn.style.display = "none";
    btn.addEventListener("click", this.handleClick);

    this.button = btn;
  }

  mount(parent: HTMLElement): void {
    if (this.disposed) return;
    if (this.parent === parent && this.button.parentElement === parent) return;
    parent.appendChild(this.button);
    this.parent = parent;
  }

  setVisible(visible: boolean): void {
    if (this.disposed) return;
    if (this.visible === visible) return;
    this.visible = visible;
    this.button.style.display = visible ? "" : "none";
  }

  setOnClick(fn: () => void): void {
    this.clickHandler = fn;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.button.removeEventListener("click", this.handleClick);
    if (this.button.parentElement !== null) {
      this.button.parentElement.removeChild(this.button);
    }
    this.parent = null;
    this.clickHandler = null;
  }
}
